import { useCallback, useRef, useState } from "react";
import api from "@/lib/api";

// Auto-save for the Task Details dialog. Immediate fields PATCH on change; free-text
// fields are debounced (~800ms). Only the changed keys are sent. Keeps unsaved values
// on failure (retry re-sends them). flush() fires any pending debounced save synchronously.
export default function useTaskAutosave(taskId, onSaved) {
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const pendingRef = useRef(null); // accumulated debounced text patch
  const failedRef = useRef(null); // last patch that failed (for retry)
  const debounceRef = useRef(null);
  const clearRef = useRef(null);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  const save = useCallback(async (patch) => {
    if (!patch || Object.keys(patch).length === 0) return;
    if (clearRef.current) { clearTimeout(clearRef.current); clearRef.current = null; }
    setStatus("saving");
    try {
      await api.patch(`/tasks/${taskId}`, patch);
      failedRef.current = null;
      setStatus("saved");
      clearRef.current = setTimeout(() => setStatus("idle"), 1800);
      onSavedRef.current?.();
    } catch (e) {
      failedRef.current = { ...(failedRef.current || {}), ...patch };
      setStatus("error");
    }
  }, [taskId]);

  const scheduleText = useCallback((patch) => {
    pendingRef.current = { ...(pendingRef.current || {}), ...patch };
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const p = pendingRef.current;
      pendingRef.current = null;
      debounceRef.current = null;
      save(p);
    }, 800);
  }, [save]);

  const flush = useCallback(() => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    if (pendingRef.current) {
      const p = pendingRef.current;
      pendingRef.current = null;
      save(p); // fire-and-forget; dialog may be closing
    }
  }, [save]);

  const retry = useCallback(() => {
    if (failedRef.current) save(failedRef.current);
  }, [save]);

  const reset = useCallback(() => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    if (clearRef.current) { clearTimeout(clearRef.current); clearRef.current = null; }
    pendingRef.current = null;
    failedRef.current = null;
    setStatus("idle");
  }, []);

  return { status, save, scheduleText, flush, retry, reset };
}
